import type * as Telemetry from "@web-monorepo/telemetry";
import { ResourceFetchError } from "@web-monorepo/dojo-fetch";
import { isNetworkFailure } from "#/src/utils/apiError";
import { logNetworkFailure } from "#/src/utils/networkFailureLog";

export const COOKIELAW_HOST = "cdn.cookielaw.org";

// telemetry's patchFetch reports every failed fetch not marked dojo.fetch.handled as a
// BareFetchError. These come from code outside marketplace whose callers already handle the
// failure, so the report is only noise:
// - Vendor scripts (OneTrust, Plausible, Zendesk) call fetch themselves and handle their own
//   failures.
// - Translation catalogs are fetched by the @web-monorepo/i18n loader, which retries them.
//   LocaleGate hands the final failure to reportCatalogError in root.tsx, which logs it and shows
//   the translation-error toast (showTranslationErrorNotification).
//
// dojoFetch throws NoNetworkError when a request fails before a response. The offline banner
// builds a second Error with that same name so ErrorPage can show the no-network page. An
// offline phone is not a server bug, and the name check below is what drops it: the banner
// error is not an instance of the dojoFetch class. BareFetchError and Axios failures keep
// their own names, so this does not drop them.
//
// dojo-fetch's resource helpers capture their own failures as a ResourceFetchError. Any whose cause
// is a no-response failure is logged as marketplace.network_error and then dropped, since nothing
// else reports it. One that got a response (a 403, a 404) still reaches Sentry.
const VENDOR_HOSTS = new Set([COOKIELAW_HOST, "plausible.classdojo.com", "ekr.zdassets.com"]);
const CATALOG_PATH = /^\/assets\/i18n-[^/]+\.json$/;

function isExpectedOfflineError(exception: unknown): boolean {
  return exception instanceof Error && exception.name === "NoNetworkError";
}

function isHandledBareFetchError(exception: unknown): boolean {
  if (!(exception instanceof Error) || exception.name !== "BareFetchError" || !("contexts" in exception)) {
    return false;
  }
  const { contexts } = exception;
  const request = typeof contexts === "object" && contexts !== null && "request" in contexts ? contexts.request : null;
  if (typeof request !== "object" || request === null) return false;
  const host = "host" in request && typeof request.host === "string" ? request.host : "";
  const pathname = "pathname" in request && typeof request.pathname === "string" ? request.pathname : "";
  return VENDOR_HOSTS.has(host) || (host === window.location.host && CATALOG_PATH.test(pathname));
}

/** Sentry beforeSend filter. Besides dropping the events above, it logs each no-response
 * ResourceFetchError it drops as marketplace.network_error. */
export const dropHandledBareFetchErrors: NonNullable<Parameters<typeof Telemetry.init>[0]["beforeSend"]> = (
  event,
  hint,
) => {
  const exception = hint.originalException;
  if (exception instanceof ResourceFetchError && isNetworkFailure(exception.cause)) {
    // ResourceFetchError's url is already stripped of its query string and ids.
    logNetworkFailure({ requestPath: exception.url, errorName: exception.name });
    return null;
  }
  return isExpectedOfflineError(exception) || isHandledBareFetchError(exception) ? null : event;
};
